import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

export const Route = createFileRoute('/parent/billing/checkout-return')({
  component: CheckoutReturn,
  validateSearch: (raw: Record<string, unknown>): CheckoutReturnSearch => {
    const status = raw.status === 'success' || raw.status === 'cancel' ? raw.status : 'success';
    const mode =
      raw.mode === 'charges' || raw.mode === 'tickets' || raw.mode === 'subscription'
        ? raw.mode
        : 'charges';
    return { status, mode };
  },
});

interface CheckoutReturnSearch {
  status: 'success' | 'cancel';
  mode: 'charges' | 'tickets' | 'subscription';
}

function CheckoutReturn() {
  const { status, mode } = Route.useSearch();
  const qc = useQueryClient();

  // Webhook-driven ledger updates are eventually consistent — they may not
  // have landed by the time the parent returns to this page. We invalidate
  // the relevant caches on mount and then refetch on focus so the next
  // navigation reflects the new state.
  useEffect(() => {
    void qc.invalidateQueries({ queryKey: ['charges'] });
    void qc.invalidateQueries({ queryKey: ['payments'] });
    void qc.invalidateQueries({ queryKey: ['family-balance'] });
    void qc.invalidateQueries({ queryKey: ['tickets'] });
    void qc.invalidateQueries({ queryKey: ['subscriptions'] });
  }, [qc]);

  if (status === 'cancel') {
    return (
      <div className="space-y-6">
        <header>
          <h1>Checkout canceled</h1>
          <p className="mt-2 text-[var(--color-ink-muted)]">
            No charge was made. You can come back any time.
          </p>
        </header>
        <Link to="/parent/billing" className="underline">
          Back to billing
        </Link>
      </div>
    );
  }

  const { heading, body } =
    mode === 'tickets'
      ? {
          heading: 'Tickets purchased',
          body: "Thanks — your seats are now yours. We'll email you a confirmation. You can also view them on the tickets page below.",
        }
      : mode === 'subscription'
        ? {
            heading: 'Auto-pay enabled',
            body: 'Thanks — your subscription is active. The first charge will appear on your card shortly, and tuition will charge automatically each month.',
          }
        : {
            heading: 'Payment received',
            body: "Thanks — Stripe is processing your payment. Your balance will update within a minute. We'll email you a receipt.",
          };

  return (
    <div className="space-y-6">
      <header>
        <h1>{heading}</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">{body}</p>
      </header>
      <div className="flex flex-wrap gap-3">
        <Link to="/parent/billing" className="underline">
          Back to billing
        </Link>
        {mode === 'tickets' && (
          <Link to="/parent/billing/tickets" className="underline">
            View tickets
          </Link>
        )}
        {mode === 'subscription' && (
          <Link to="/parent/billing/auto-pay" className="underline">
            Manage auto-pay
          </Link>
        )}
      </div>
    </div>
  );
}
